import React, { useRef, useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Toolbar from './Toolbar';
import WordCounter from './WordCounter';
import './TextEditor.css';

const TextEditor = ({
  value,
  onChange,
  onSave,
  onPrint,
  onDirtyChange,
  placeholder,
  readOnly = false,
  className,
  style,
}) => {
  const contentRef = useRef(null);
  const skipSyncRef = useRef(false);
  const lastSentRef = useRef(value);

  const [activeCommands, setActiveCommands] = useState(new Set());
  const [plainText, setPlainText] = useState('');
  const [currentBlock, setCurrentBlock] = useState('p');
  const [currentFont, setCurrentFont] = useState('Times New Roman');
  const [currentSize, setCurrentSize] = useState('3');

  // ── Init & external value sync ──────────────────────────────────────────────
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    el.innerHTML = value;
    setPlainText(el.innerText || el.textContent || '');
    if (onDirtyChange) onDirtyChange(false);
  }, []);

  useEffect(() => {
    if (skipSyncRef.current) {
      // Only skip when the incoming value is the one we just emitted.
      if (value === lastSentRef.current) {
        skipSyncRef.current = false;
        return;
      }
      skipSyncRef.current = false;
    }
    if (value !== lastSentRef.current) {
      const el = contentRef.current;
      if (el) {
        el.innerHTML = value;
        setPlainText(el.innerText || el.textContent || '');
      }
      lastSentRef.current = value;
    }
  }, [value]);

  // ── Toolbar state ───────────────────────────────────────────────────────────
  const updateToolbarState = useCallback(() => {
    try {
      if (typeof document.queryCommandState !== 'function') return;
      const active = new Set(
        ['bold', 'italic', 'underline'].filter((cmd) => document.queryCommandState(cmd))
      );
      // Detect active alignment: walk up from anchor, stop before content div
      // (content div never holds alignment — cleared on every apply).
      const sel = window.getSelection();
      const anchorNode = sel && sel.anchorNode;
      if (anchorNode && contentRef.current && contentRef.current.contains(anchorNode)) {
        let node = anchorNode.nodeType === Node.TEXT_NODE
          ? anchorNode.parentElement
          : anchorNode;
        let align = '';
        while (node && node !== contentRef.current) {
          const explicitAlign = node.style && node.style.textAlign;
          if (explicitAlign) {
            align = explicitAlign;
            break;
          }
          node = node.parentNode;
        }
        // If cursor is directly inside the content div (text with no block wrapper),
        // check content's own textAlign — it's the only way to store alignment there.
        if (!align) {
          const immediateParent = anchorNode.nodeType === Node.TEXT_NODE
            ? anchorNode.parentElement
            : anchorNode;
          if (immediateParent === contentRef.current) {
            align = contentRef.current.style.textAlign || '';
          }
        }
        if (align === 'center') active.add('justifyCenter');
        else if (align === 'right') active.add('justifyRight');
        else if (align === 'justify') active.add('justifyFull');
        else active.add('justifyLeft');
      }
      setActiveCommands(active);
      if (typeof document.queryCommandValue === 'function') {
        const blockValue = document.queryCommandValue('formatBlock');
        const block = (blockValue || '').toLowerCase();
        setCurrentBlock(['h1', 'h2', 'h3', 'h4'].includes(block) ? block : 'p');
        const fontCommandValue = document.queryCommandValue('fontName');
        let font = (fontCommandValue || '')
          .split(',')[0]
          .trim()
          .replace(/['"]/g, '');
        if (!font) {
          const sel = window.getSelection();
          const node = sel && sel.anchorNode;
          const el = node
            ? (node.nodeType === Node.TEXT_NODE ? node.parentElement : node)
            : null;
          if (el) {
            font = window
              .getComputedStyle(el)
              .fontFamily
              .split(',')[0]
              .trim()
              .replace(/['"]/g, '');
          }
        }
        setCurrentFont(font || 'Times New Roman');
        const size = document.queryCommandValue('fontSize');
        setCurrentSize(size || '3');
      }
    } catch {
      // queryCommand can throw outside user gesture
    }
  }, []);

  useEffect(() => {
    const onSelectionChange = () => {
      if (!contentRef.current) return;
      const sel = document.getSelection();
      const node = sel && sel.anchorNode;
      if (node && contentRef.current.contains(node)) updateToolbarState();
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, [updateToolbarState]);

  // ── Input handler ───────────────────────────────────────────────────────────
  const handleInput = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    skipSyncRef.current = true;
    const html = el.innerHTML;
    lastSentRef.current = html;
    onChange(html);
    if (onDirtyChange) onDirtyChange(true);
    setPlainText(el.innerText || el.textContent || '');
    updateToolbarState();
  }, [onChange, onDirtyChange, updateToolbarState]);

  // ── Paste handler: strip Chrome wrapper divs ────────────────────────────────
  const handlePaste = useCallback((e) => {
    const html = e.clipboardData && e.clipboardData.getData('text/html');
    if (!html) return;

    e.preventDefault();
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    tmp.querySelectorAll('meta, style, script, link').forEach((el) => el.remove());

    // Unwrap outermost Chrome wrapper div(s)
    let unwrapped = true;
    while (unwrapped) {
      unwrapped = false;
      if (tmp.children.length === 1 && tmp.firstElementChild && tmp.firstElementChild.tagName === 'DIV') {
        const only = tmp.firstElementChild;
        const nodes = Array.from(only.childNodes);
        for (const n of nodes) tmp.insertBefore(n, only);
        tmp.removeChild(only);
        unwrapped = true;
      }
    }

    document.execCommand('insertHTML', false, tmp.innerHTML);
  }, []);

  // ── Format handler ──────────────────────────────────────────────────────────
  const removeStyleFromSelection = useCallback(
    (property) => {
      const el = contentRef.current;
      if (!el) return;
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_ELEMENT);
      const nodes = [];
      let n = walker.nextNode();
      while (n) {
        if (range.intersectsNode(n)) nodes.push(n);
        n = walker.nextNode();
      }
      nodes.forEach((node) => {
        node.style.removeProperty(property);
        if (property === 'color' && node.tagName === 'FONT') node.removeAttribute('color');
        if (!node.style.cssText) node.removeAttribute('style');
      });
      Array.from(el.querySelectorAll('span, font')).reverse().forEach((node) => {
        if (node.attributes.length === 0) node.replaceWith(...Array.from(node.childNodes));
      });
      el.querySelectorAll('span, font').forEach((node) => {
        if (node.attributes.length === 0 && node.childNodes.length === 0) node.remove();
      });
      el.normalize();
      skipSyncRef.current = true;
      const html = el.innerHTML;
      lastSentRef.current = html;
      onChange(html);
      if (onDirtyChange) onDirtyChange(true);
      setPlainText(el.innerText || el.textContent || '');
    },
    [onChange, onDirtyChange]
  );

  const handleFormat = useCallback(
    (command, val) => {
      if (command === 'formatBlock') {
        document.execCommand('formatBlock', false, val || 'p');
      } else if (command === 'fontName') {
        document.execCommand('fontName', false, val || 'Times New Roman');
        const sel = window.getSelection();
        const node = sel && sel.anchorNode;
        const el = node
          ? (node.nodeType === Node.TEXT_NODE ? node.parentElement : node)
          : null;
        if (el) {
          const li = el.closest ? el.closest('li') : null;
          if (li) {
            const computed = window.getComputedStyle(el);
            li.style.fontFamily = computed.fontFamily;
          }
        }
      } else if (command === 'fontSize') {
        document.execCommand('styleWithCSS', false, 'true');
        document.execCommand('fontSize', false, val || '3');
        const sel = window.getSelection();
        const node = sel && sel.anchorNode;
        const el = node
          ? (node.nodeType === Node.TEXT_NODE ? node.parentElement : node)
          : null;
        if (el) {
          const li = el.closest ? el.closest('li') : null;
          if (li) {
            const computed = window.getComputedStyle(el);
            li.style.fontSize = computed.fontSize;
          }
        }
      } else if (command === 'foreColor') {
        document.execCommand('foreColor', false, val || '#000000');
      } else if (command === 'resetColor') {
        removeStyleFromSelection('color');
        return;
      } else if (command === 'hiliteColor') {
        document.execCommand('hiliteColor', false, val || '#ffff00');
      } else if (command === 'resetHighlight') {
        removeStyleFromSelection('background-color');
        return;
      } else if (['justifyLeft', 'justifyCenter', 'justifyRight', 'justifyFull'].includes(command)) {
        const alignMap = {
          justifyLeft: 'left',
          justifyCenter: 'center',
          justifyRight: 'right',
          justifyFull: 'justify',
        };
        const align = alignMap[command];
        const sel = window.getSelection();
        const content = contentRef.current;
        if (sel && sel.rangeCount > 0 && content) {
          const range = sel.getRangeAt(0);
          const BLOCK_TAGS = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'BLOCKQUOTE', 'PRE']);
          const blockAncestor = (node) => {
            let n = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
            while (n && n !== content) {
              if (n.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has(n.tagName)) {
                return n;
              }
              n = n.parentNode;
            }
            return content;
          };
          const startBlock = blockAncestor(range.startContainer);
          const endBlock = blockAncestor(range.endContainer);
          if (startBlock === endBlock) {
            // Single block — may be content itself for unwrapped text (no Enter pressed yet)
            startBlock.style.textAlign = align;
            if (startBlock !== content) content.style.removeProperty('text-align');
          } else {
            // Multi-block: NEVER set textAlign on the content div itself.
            // Setting it there bleeds to every child element via CSS cascade and
            // causes "justify always active" across the whole document.
            // If the selection starts in unwrapped text (startBlock === content),
            // begin from the first real child block instead.
            const childBlocks = Array.from(
              content.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6, li')
            );
            let inside = startBlock === content; // start immediately if unwrapped text begins selection
            for (const block of childBlocks) {
              if (!inside && block === startBlock) inside = true;
              if (inside) block.style.textAlign = align;
              if (block === endBlock) break;
            }
            // Clear any stale textAlign on the content div
            content.style.removeProperty('text-align');
          }
        }
      } else if (command === 'insertTable') {
        const parts = (val || '3x3').split('x').map(Number);
        const rows = parts[0];
        const cols = parts[1];
        const cell = '<td style="border:1px solid #d1d5db;padding:6px 12px;min-width:60px;">&nbsp;</td>';
        const row = `<tr>${cell.repeat(cols)}</tr>`;
        const table = `<br><table style="border-collapse:collapse;width:100%;">${row.repeat(rows)}</table><br>`;
        document.execCommand('insertHTML', false, table);
      } else {
        document.execCommand(command, false);
      }
      updateToolbarState();
      const el = contentRef.current;
      if (el) {
        skipSyncRef.current = true;
        const html = el.innerHTML;
        lastSentRef.current = html;
        onChange(html);
        if (onDirtyChange) onDirtyChange(true);
      }
    },
    [onChange, onDirtyChange, updateToolbarState, removeStyleFromSelection]
  );

  const handleSave = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const html = el.innerHTML;
    lastSentRef.current = html;
    onChange(html);
    if (onSave) onSave(html);
    if (onDirtyChange) onDirtyChange(false);
  }, [onChange, onDirtyChange, onSave]);

  const handlePrint = useCallback(() => {
    if (onPrint) {
      onPrint();
      return;
    }
    window.print();
  }, [onPrint]);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className={`text-editor${className ? ` ${className}` : ''}`} style={style}>
      <Toolbar
        onFormat={handleFormat}
        onSave={handleSave}
        onPrint={handlePrint}
        activeCommands={activeCommands}
        currentBlock={currentBlock}
        currentFont={currentFont}
        currentSize={currentSize}
      />
      <div
        ref={contentRef}
        className="text-editor__content"
        contentEditable={!readOnly}
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={handleInput}
        onPaste={handlePaste}
        onKeyUp={updateToolbarState}
        onMouseUp={updateToolbarState}
      />
      <WordCounter content={plainText} />
    </div>
  );
};

TextEditor.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  onPrint: PropTypes.func,
  onDirtyChange: PropTypes.func,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
};

export default TextEditor;
